import type { ApexOptions } from 'apexcharts'
import Image from 'next/image'
import { FunctionComponent, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  SlideContainer,
  SlideTitle,
  Card,
  CardTitle,
} from '@/utils/export-pptx/components/slides/styles'
import {
  chartSize,
  PptxObjectType,
  SPECIAL_CHAR,
} from '@/utils/export-pptx/constants'
import useApexChartDataUri from '@/utils/export-pptx/hooks/use-apex-chart-data-uri'
import type { CommentSentimentRatioSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: CommentSentimentRatioSlide
  onSlideReady?: (slide: CommentSentimentRatioSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  const options: ApexOptions = useMemo(() => {
    const { positive, negative, neutral, unknown, categories } =
      slide.payload.kolList.reduce<{
        positive: number[]
        negative: number[]
        neutral: number[]
        unknown: number[]
        categories: string[]
      }>(
        (acc, item) => {
          acc.categories.push(item.name.replaceAll(SPECIAL_CHAR, ''))
          acc.positive.push(item.positive)
          acc.negative.push(item.negative)
          acc.neutral.push(item.neutral)
          acc.unknown.push(item.unknown)

          return acc
        },
        {
          positive: [],
          negative: [],
          neutral: [],
          unknown: [],
          categories: [],
        },
      )

    return {
      series: [
        {
          name: formatMessage({ id: 'autoreport:chart_field_positive' }),
          data: positive,
        },
        {
          name: formatMessage({ id: 'autoreport:chart_field_negative' }),
          data: negative,
        },
        {
          name: formatMessage({ id: 'autoreport:chart_field_neutral' }),
          data: neutral,
        },
        {
          name: formatMessage({ id: 'autoreport:chart_field_unknown' }),
          data: unknown,
        },
      ],
      chart: {
        type: 'bar',
        height: chartSize.stackedColumns.h,
        width: chartSize.stackedColumns.w,
        stacked: true,
        stackType: '100%',
      },
      xaxis: { categories },
      yaxis: {
        labels: { formatter: (value) => `${value}%` },
      },
      dataLabels: { enabled: false },
    }
  }, [formatMessage, slide.payload.kolList])

  const dataUri = useApexChartDataUri(options)

  useEffect(() => {
    if (!dataUri) {
      return
    }

    onSlideReady?.(slide)
  }, [onSlideReady, slide, dataUri])

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape} mb={12}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'autoreport:analysis_section_comment' })}
        </span>
      </SlideTitle>

      <Card data-pptx-object={PptxObjectType.Shape}>
        <CardTitle data-pptx-object={PptxObjectType.Text}>
          {slide.payload.title}
        </CardTitle>

        <ChartContainer>
          {dataUri && (
            <Image
              data-original-size
              alt='kol-interaction-rate-chart'
              data-pptx-object={PptxObjectType.Image}
              height={chartSize.stackedColumns.h}
              src={dataUri}
              width={chartSize.stackedColumns.w}
            />
          )}
        </ChartContainer>
      </Card>
    </SlideContainer>
  )
}

const ChartContainer = styled.div`
  height: ${chartSize.stackedColumns.h}px;
`

export default SlideComponent
